/**
 * Data Import Wizard — safe-assignment lattice (S6.T1 / Fable CP4 addendum).
 *
 * `canAssign(source, target)` decides whether a source file column can be
 * safely assigned into an EXISTING target table column. This is deliberately
 * NOT the same question as `columnTypesEqualAcrossEngines` (cross-engine
 * schema-compare EQUALITY) — that function must never be used here.
 *
 * Design (Fable CP4, authoritative — see `Docs/DataImport.md` §D3 / the task
 * brief's addendum): **default to `invalid`.** Only pairs explicitly promoted
 * below return `ok`/`lossy`; everything else — the binary/varbinary/blob
 * family as a target for any scalar source, `unknown`-kind targets (spatial /
 * array / xml / enum all arrive as `kind:'unknown'`), json→scalar, and any
 * other combination with no stated rule — is `invalid`. Numerics are built as
 * capacity comparisons (integer/float ladders, decimal digit-capacity, an
 * int→float mantissa check), not hand-listed pairs.
 *
 * Inference only ever emits a limited set of SOURCE kinds (never int8/int16,
 * float32, char, or the binary family) — see the brief's "Structural fact".
 * The TARGET can be anything (an existing table's real column). This
 * function is still total over the full `CanonicalTypeKind` space.
 *
 * Process-agnostic shared module (src/features/workspace/data-import/mapping/,
 * relocated S6.T2a): no React/zustand/DOM/electron-runtime imports — both the
 * renderer and the main process import this SAME code. `CanonicalType`/
 * `CanonicalTypeKind` are type-only imports, erased at build — routed through
 * `src/types/electron.d.ts` (the codebase's sanctioned crossing point) rather
 * than a direct `electron/cross-engine/canonicalType` import, which trips the
 * tsconfig.node.json composite project-reference boundary (TS6305) for a
 * fully-checked `src/` file.
 */
import type { CanonicalType, CanonicalTypeKind } from '../../../../types/electron';
import {
  BINARY_KINDS,
  CHAR_KINDS,
  FLOAT_KINDS,
  INT_KINDS,
  STRINGIFIABLE_SCALAR_KINDS,
  decimalVerdict,
  floatLadderVerdict,
  intLadderVerdict,
  intToFloatVerdict,
  lengthVerdict,
  stringifiedScalarToCharVerdict,
  uuidToCharVerdict,
} from './assignabilityLattice';

export type AssignabilityVerdict = 'ok' | 'lossy' | 'invalid';

type IntKind = 'int8' | 'int16' | 'int32' | 'int64';
type FloatKind = 'float32' | 'float64';

const isInt = (k: CanonicalTypeKind): k is IntKind => INT_KINDS.has(k);
const isFloat = (k: CanonicalTypeKind): k is FloatKind => FLOAT_KINDS.has(k);
const isChar = (k: CanonicalTypeKind): boolean => CHAR_KINDS.has(k);
const isBinary = (k: CanonicalTypeKind): boolean => BINARY_KINDS.has(k);

export function canAssign(source: CanonicalType, target: CanonicalType): AssignabilityVerdict {
  // `unknown` targets (spatial/array/xml/enum) are never a safe assignment —
  // checked first so it wins over every other branch below, including a
  // matching `unknown` source.
  if (target.kind === 'unknown') return 'invalid';

  // Binary family is an island: assignable only among itself (length rule);
  // never cross into/out of any scalar/character kind.
  if (isBinary(target.kind) || isBinary(source.kind)) {
    return isBinary(target.kind) && isBinary(source.kind) ? lengthVerdict(source, target) : 'invalid';
  }

  // Character-family TARGET (char/varchar/text).
  if (isChar(target.kind)) {
    if (isChar(source.kind)) return lengthVerdict(source, target);
    if (source.kind === 'uuid') return uuidToCharVerdict(target);
    // Every other stringifiable scalar is length-aware into a BOUNDED target
    // (an undersized varchar/char truncates a large int/decimal/timestamp, and
    // json is unbounded like text) — never a flat 'ok'. Unbounded target → ok.
    if (STRINGIFIABLE_SCALAR_KINDS.has(source.kind)) return stringifiedScalarToCharVerdict(source, target);
    return 'invalid'; // e.g. an `unknown` source stringified is not assumed safe
  }

  // Character-family SOURCE into a non-character target.
  if (isChar(source.kind)) {
    if (target.kind === 'boolean') return 'lossy';
    if (target.kind === 'uuid') return 'lossy';
    if (target.kind === 'json') return 'lossy';
    return 'invalid'; // e.g. varchar -> int/date/etc — parse risk, not promoted
  }

  if (source.kind === 'uuid') return target.kind === 'uuid' ? 'ok' : 'invalid';
  if (source.kind === 'json') return target.kind === 'json' ? 'ok' : 'invalid';

  if (source.kind === 'boolean') {
    if (target.kind === 'boolean') return 'ok';
    if (isInt(target.kind) || target.kind === 'decimal' || isFloat(target.kind)) return 'ok';
    return 'invalid';
  }

  if (isInt(source.kind)) {
    if (target.kind === 'boolean') return 'lossy';
    if (isInt(target.kind)) return intLadderVerdict(source.kind, target.kind);
    if (isFloat(target.kind)) return intToFloatVerdict(source.kind, target.kind);
    if (target.kind === 'decimal') return 'ok';
    return 'invalid';
  }

  if (source.kind === 'decimal') {
    if (isInt(target.kind)) return 'lossy';
    if (isFloat(target.kind)) return 'lossy';
    if (target.kind === 'decimal') return decimalVerdict(source, target);
    return 'invalid';
  }

  if (isFloat(source.kind)) {
    if (isInt(target.kind)) return 'lossy';
    if (target.kind === 'decimal') return 'lossy';
    if (isFloat(target.kind)) return floatLadderVerdict(source.kind, target.kind);
    return 'invalid';
  }

  if (source.kind === 'date') {
    if (target.kind === 'date') return 'ok';
    if (target.kind === 'timestamp') return 'ok';
    if (target.kind === 'timestamptz') return 'ok';
    return 'invalid'; // e.g. date -> time is not defined
  }

  if (source.kind === 'time') {
    return target.kind === 'time' ? 'ok' : 'invalid'; // time -> date/timestamp explicitly invalid
  }

  if (source.kind === 'timestamp') {
    if (target.kind === 'timestamp') return 'ok';
    // Load-bearing on the writer's UTC-anchoring (see cellCoercion.ts's
    // per-engine naive-datetime UTC anchoring, S5 real-DB fix): if coercion
    // ever stops anchoring a naive timestamp as UTC before binding it, this
    // verdict must flip to 'lossy'.
    if (target.kind === 'timestamptz') return 'ok';
    if (target.kind === 'date') return 'lossy';
    if (target.kind === 'time') return 'lossy';
    return 'invalid';
  }

  if (source.kind === 'timestamptz') {
    if (target.kind === 'timestamptz') return 'ok';
    if (target.kind === 'timestamp') return 'lossy'; // zone dropped
    return 'invalid'; // e.g. timestamptz -> date/time not defined
  }

  return 'invalid'; // unreachable given CanonicalTypeKind's fixed union; safety net
}
